// How the viewer likes it to look: the chat typeface, the theme, the show and whether things move,
// remembered in this browser (the head applies them before first paint; this keeps them and the
// controls under the gear and on the settings page in step).
import { motionOn, setMotion } from "./dom.ts";
import { FONT_KEY, onBroadcast, THEME_KEY } from "./env.ts";
import { readPref, writePref } from "./sound/prefs.ts";
import { getShow, isShow, setShow } from "./show.ts";
import type { Show } from "./show.ts";

/** the theme (#437): "system" (the device's), "light" or "dark"; the head applies it before first paint (boot.ts) */
type Theme = "system" | "light" | "dark";
const getTheme = (): Theme => {
  const v = readPref(THEME_KEY);
  return v === "light" || v === "dark" ? v : "system";
};
/** the theme the page wears: the choice, or the device's (dark unless it asks for light); a broadcast is always dark */
export const resolveTheme = (t: Theme, deviceLight: boolean, broadcast: boolean): "light" | "dark" =>
  broadcast ? "dark" : t === "system" ? (deviceLight ? "light" : "dark") : t;
const lightQuery = () =>
  typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: light)") : null;
/** the sheet on the page; a change is announced (`basho:theme`) so what paints its own colours (the stage) repaints */
const applyTheme = () => {
  const h = document.documentElement,
    was = h.dataset.theme;
  h.dataset.theme = resolveTheme(getTheme(), !!lightQuery()?.matches, onBroadcast());
  if (h.dataset.theme !== was) document.dispatchEvent(new window.Event("basho:theme"));
};
let themeWatched = false;

const apply = () => {
  const v = readPref(FONT_KEY),
    h = document.documentElement;
  if (v) h.setAttribute("data-font", v);
  else h.removeAttribute("data-font");
};

/** every control for the face says the same thing */
const showFont = () => {
  const cur = readPref(FONT_KEY) || "mono";
  document.querySelectorAll("[data-look] input[type=radio]").forEach((o) => {
    (o as HTMLInputElement).checked = (o as HTMLInputElement).value === cur;
  });
  document.querySelectorAll("select[data-look]").forEach((o) => {
    (o as HTMLSelectElement).value = cur;
  });
};
const chooseFont = (v: string) => {
  writePref(FONT_KEY, v);
  apply();
  showFont();
};
// the switches only: <html data-motion="off"> carries the same attribute (the css reads it), and a listener bound to it
// took every switch's change as it bubbled up and turned the animations straight back off
const showMotion = () => {
  document.querySelectorAll("input[data-motion]").forEach((o) => {
    (o as HTMLInputElement).checked = motionOn();
  });
};

/** the show's switch was thrown: the page is told (main.js mounts the stage, or clears it) */
const chooseShow = (v: Show) => {
  setShow(v);
  document.dispatchEvent(new window.Event("basho:show"));
  document.querySelectorAll("[data-stage]").forEach((o) => {
    (o as HTMLInputElement).checked = v === "stage";
  }); // the mixer's and the gear's, in step
  document.querySelectorAll(`[data-show] input[type=radio][value="${v}"]`).forEach((o) => {
    (o as HTMLInputElement).checked = true;
  });
};

/** the radios on the settings page (in `root`, so a soft navigation can rebind), and the show's switches: the radios on /settings, the checkbox in the mixer */
export function initLook(root: ParentNode = document) {
  apply();
  applyTheme();
  // "system": the page follows the device as it changes (once, however often the page is bound)
  if (!themeWatched) {
    themeWatched = true;
    lightQuery()?.addEventListener("change", () => {
      if (getTheme() === "system") applyTheme();
    });
  }
  root.querySelectorAll("[data-theme-choice]").forEach((el) => {
    const radios = el.querySelectorAll("input[type=radio]") as NodeListOf<HTMLInputElement>;
    radios.forEach((r) => {
      r.checked = r.value === getTheme();
      r.addEventListener("change", () => {
        if (!r.checked || (r.value !== "system" && r.value !== "light" && r.value !== "dark")) return;
        writePref(THEME_KEY, r.value);
        applyTheme();
        // the other set of radios (the gear's, the settings page's) in step
        document.querySelectorAll(`[data-theme-choice] input[type=radio][value="${r.value}"]`).forEach((o) => {
          (o as HTMLInputElement).checked = true;
        });
      });
    });
  });
  // the gear: the quick settings in a popover (Sam, #437: "tap gear icon, get list… you should not have to leave the page")
  root.querySelectorAll("[data-gear]").forEach((el) => {
    const btn = el as HTMLButtonElement,
      pop = btn.parentElement?.querySelector("[data-setpop]");
    // wired once: its listeners on the document live as long as the page, so a second binding would double them
    if (!(pop instanceof HTMLElement) || btn.dataset.gearWired) return;
    btn.dataset.gearWired = "1";
    const open = (v: boolean) => {
      pop.hidden = !v;
      btn.setAttribute("aria-expanded", String(v));
    };
    btn.addEventListener("click", () => open(pop.hidden));
    document.addEventListener("pointerdown", (e) => {
      if (!pop.hidden && e.target instanceof Node && !pop.contains(e.target) && !btn.contains(e.target)) open(false);
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !pop.hidden) {
        open(false);
        btn.focus();
      }
    });
  });
  // the gear's "the rules card" (the app): the first-bout card again (static/js/intro.ts)
  root.querySelectorAll("[data-intro]").forEach((el) => {
    el.addEventListener("click", () => void import("./intro.ts").then((m) => m.show(true)));
  });
  root.querySelectorAll("[data-show]").forEach((el) => {
    const radios = el.querySelectorAll("input[type=radio]") as NodeListOf<HTMLInputElement>;
    radios.forEach((r) => {
      r.checked = r.value === getShow();
      r.addEventListener("change", () => {
        if (r.checked && isShow(r.value)) chooseShow(r.value);
      });
    });
  });
  root.querySelectorAll("[data-stage]").forEach((el) => {
    const c = el as HTMLInputElement;
    c.checked = getShow() === "stage";
    c.addEventListener("change", () => chooseShow(c.checked ? "stage" : "classic"));
  });
  // the face: radios on the settings page, a select under the gear
  root.querySelectorAll("[data-look]").forEach((el) => {
    if (el.tagName === "SELECT") {
      const sel = el as HTMLSelectElement;
      sel.addEventListener("change", () => chooseFont(sel.value));
      return;
    }
    (el.querySelectorAll("input[type=radio]") as NodeListOf<HTMLInputElement>).forEach((r) => {
      r.addEventListener("change", () => {
        if (r.checked) chooseFont(r.value);
      });
    });
  });
  showFont();
  // the animations: a switch under the gear and on the settings page
  root.querySelectorAll("input[data-motion]").forEach((el) => {
    const c = el as HTMLInputElement;
    c.addEventListener("change", () => {
      setMotion(c.checked);
      showMotion();
    });
  });
  showMotion();
}
